import React from 'react';
import { TAILLE_TEXTE } from './echartsTheme';
import { couleurMonochromeContinue, couleurDivergenteContinue } from '../constants';

/**
 * Sous-ensemble de `palette` réellement couvert par l'intervalle [min, max] :
 * si toutes les valeurs sont positives (ou toutes négatives), les paliers de
 * l'autre côté du zéro ne doivent pas apparaître dans la légende. Même
 * logique que CarteRisquePRA (dupliquée volontairement : ce sont deux petits
 * composants indépendants, pas de fichier utilitaire commun pour si peu).
 * Ne concerne que le mode `palette` (discret) — le mode `continu` a sa
 * propre construction de dégradé CSS ci-dessous.
 */
function paliersVisibles(palette, min, max) {
  const indexPour = (valeur) => {
    const idx = palette.findIndex((p) => valeur > p.seuil);
    return idx === -1 ? palette.length - 1 : idx;
  };
  const debut = Math.min(indexPour(max), indexPour(min));
  const fin = Math.max(indexPour(max), indexPour(min));
  return palette.slice(debut, fin + 1);
}

/**
 * Dégradé CSS (stops en %) pour le mode continu. Mono : min -> base (couleur
 * centrale) -> max. Diverge : min -> [blanc pile à la position de 0, si 0
 * est bien entre min et max] -> max ; si toutes les valeurs sont du même
 * signe, l'autre couleur n'entre jamais dans le dégradé.
 */
function degradeContinuCss(continu, min, max) {
  if (max === min) return continu.type === 'diverge'
    ? couleurDivergenteContinue(continu.positif, continu.negatif, min, max, min)
    : continu.base;

  if (continu.type !== 'diverge') {
    const mid = (min + max) / 2;
    const position = ((mid - min) / (max - min)) * 100;
    return `linear-gradient(to right, ${couleurMonochromeContinue(continu.base, min, max, min)} 0%, ${continu.base} ${position}%, ${couleurMonochromeContinue(continu.base, min, max, max)} 100%)`;
  }

  const couleur = (v) => couleurDivergenteContinue(continu.positif, continu.negatif, min, max, v);
  if (min >= 0 || max <= 0) {
    // Toutes les valeurs du même signe : dégradé simple entre les 2 bornes.
    return `linear-gradient(to right, ${couleur(min)}, ${couleur(max)})`;
  }
  const positionZero = ((0 - min) / (max - min)) * 100;
  return `linear-gradient(to right, ${couleur(min)} 0%, #ffffff ${positionZero}%, ${couleur(max)} 100%)`;
}

/**
 * Légende unique pour un ensemble de cartes côte à côte qui partagent la même
 * échelle de couleur (Comparer / Phénologie, Fréquence aléas, Eau) — chaque
 * carte individuelle passe `afficherLegende={false}` pour ne pas en afficher
 * une chacune.
 *
 * @param palette  mode discret (ancien) : la même palette que celle passée à
 *                 CarteRisquePRA. Ignoré si `continu` est fourni.
 * @param continu  mode continu (nouveau, Phénologie/Fréquence aléas/Eau) :
 *                 même forme que la prop `continu` de CarteRisquePRA.
 * @param min/max  bornes affichées aux extrémités du dégradé — calculées par
 *                 la page appelante sur l'ensemble des cartes (pas une seule)
 * @param unite    unité affichée (par défaut %)
 */
export default function LegendeCarte({ palette, continu, min, max, unite = '%' }) {
  return (
    <div className="carte-pra-legende">
      <span style={{ fontSize: TAILLE_TEXTE }}>{min} {unite}</span>
      <div className="carte-pra-degrade">
        {continu ? (
          <span style={{ flex: 1, background: degradeContinuCss(continu, min, max) }} />
        ) : (
          [...paliersVisibles(palette, min, max)].reverse().map((p) => (
            <span key={p.seuil} style={{ backgroundColor: p.couleur }} />
          ))
        )}
      </div>
      <span style={{ fontSize: TAILLE_TEXTE }}>{max} {unite}</span>
    </div>
  );
}
